import { useContext, useEffect } from "react";
import { AppContext } from "../contexts/AppContext";
import { observer } from "mobx-react-lite";
import useRestricted from "../hooks/useRestricted";
import ImageColumGrid from "../components/common/ImageColumGrid";
import React from "react";
import { useIntl } from "react-intl";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import DirectionsRun from "@mui/icons-material/DirectionsRun";
import DirectionsBike from "@mui/icons-material/DirectionsBike";
import posthog from "posthog-js";
import { useNavigate } from "react-router-dom";

const Account: React.FC = observer(() => {

  useRestricted();

  const navigate = useNavigate();

  const { userService, firebaseService } = useContext(AppContext);
  const intl = useIntl();

  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const [name, setName] = React.useState<string>("");
  const [language, setLanguage] = React.useState<string>("");
  const [distanceUnits, setDistanceUnits] = React.useState<string>("");
  const [elevationUnits, setElevationUnits] = React.useState<string>("");
  const [temperatureUnits, setTemperatureUnits] = React.useState<string>("");
  const [preferedSport, setPreferedSport] = React.useState<"run" | "bike" | undefined>();

  useEffect(() => {
    if (userService.user) {
      setName(userService.user.displayName || "");
      setLanguage(userService.user.language || "en");
      setDistanceUnits(userService.user.distanceUnits || "km");
      setElevationUnits(userService.user.elevationUnits || "m");
      setTemperatureUnits(userService.user.temperatureUnits || "C");
      setPreferedSport(userService.user.preferedSport || "run");
    }
  }, [userService.user]);

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    posthog.capture('account_name_changed');
    setName(event.target.value);
  }

  const handleChangeLanguage = (
    event: React.MouseEvent<HTMLElement>,
    newLang: string | null,
  ) => {
    posthog.capture('account_language_changed', { language: newLang });
    if (newLang !== null) {
      setLanguage(newLang);
    }
  };

  const handleChangeDistanceUnits = (
    event: React.MouseEvent<HTMLElement>,
    newDistanceUnits: string | null,
  ) => {
    posthog.capture('account_distance_units_changed', { distanceUnits: newDistanceUnits });
    if (newDistanceUnits !== null) {
      setDistanceUnits(newDistanceUnits);
    }
  };

  const handleChangeElevationUnits = (
    event: React.MouseEvent<HTMLElement>,
    newElevationUnits: string | null,
  ) => {
    posthog.capture('account_elevation_units_changed', { elevationUnits: newElevationUnits });
    if (newElevationUnits !== null) {
      setElevationUnits(newElevationUnits);
    }
  };

  const handleChangeTemperatureUnits = (
    event: React.MouseEvent<HTMLElement>,
    newTemperatureUnits: string | null,
  ) => {
    posthog.capture('account_temperature_units_changed', { temperatureUnits: newTemperatureUnits });
    if (newTemperatureUnits !== null) {
      setTemperatureUnits(newTemperatureUnits);
    }
  };

  const handleChangePreferedSport = (
    event: React.MouseEvent<HTMLElement>,
    newPreferedSport: "run" | "bike" | null,
  ) => {
    posthog.capture('account_prefered_sport_changed', { preferedSport: newPreferedSport });
    if (newPreferedSport !== null) {
      setPreferedSport(newPreferedSport);
    }
  }

  const handleEditUser = async (event: React.FormEvent<HTMLFormElement>) => {
    posthog.capture('account_edit_user');
    setLoading(true);
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (!data.get('name')) {
      setError(intl.formatMessage({ id: "error.nameRequired" }));
      setLoading(false);
      return;
    }

    try {
      if (!userService.user) {
        setError(intl.formatMessage({ id: "error.unknown" }));
        setLoading(false);
        return;
      }
      await userService.updateUserData({ 
        ...userService.user, 
        displayName: data.get('name') as string, 
        language, 
        distanceUnits, 
        elevationUnits, 
        temperatureUnits, 
        preferedSport 
      });
      setLoading(false);
    } catch (error) {
      setError(intl.formatMessage({ id: "error.unknown" }));
      firebaseService.onEvent("exception", { description: error, userId: userService.user?.id });
      setLoading(false);
    }
  };

  return (
    <ImageColumGrid
      image={`${process.env.PUBLIC_URL}/images/auth-background${userService.user?.preferedSport === "bike" ? "-bike" : ""}.jpg`}
    >
      {userService.user && (<Box
        sx={{
          my: 8,
          mx: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          {intl.formatMessage({ id: "account.title" })}
        </Typography>
        <Box component="form" noValidate onSubmit={handleEditUser} sx={{ mt: 1 }} ref={formRef}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={intl.formatMessage({ id: "form.email" })}
            name="email"
            autoComplete="email"
            disabled
            onChange={() => setError(null)}
            value={userService.user.email}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label={intl.formatMessage({ id: "form.name" })}
            name="name"
            autoComplete="name"
            autoFocus
            onChange={handleChangeName}
            value={name}
          />

          <Box display="flex" alignItems="center" justifyContent="space-between" m={1}>

            <Typography variant="body2">
              {intl.formatMessage({ id: "form.language" })}
            </Typography>
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={handleChangeLanguage}
              aria-label="language"
            >
              <ToggleButton value="en" aria-label="english">
                EN
              </ToggleButton>
              <ToggleButton value="es" aria-label="spanish">
                ES
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" m={1}>

            <Typography variant="body2">
              {intl.formatMessage({ id: "form.distanceUnits" })}
            </Typography>
            <ToggleButtonGroup
              value={distanceUnits}
              exclusive
              onChange={handleChangeDistanceUnits}
              aria-label="distance units"
            >
              <ToggleButton value="km" aria-label="kilometers">
                {intl.formatMessage({ id: "km" })}
              </ToggleButton>
              <ToggleButton value="miles" aria-label="miles">
                {intl.formatMessage({ id: "miles" })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" m={1}>

            <Typography variant="body2">
              {intl.formatMessage({ id: "form.elevationUnits" })}
            </Typography>
            <ToggleButtonGroup
              value={elevationUnits}
              exclusive
              onChange={handleChangeElevationUnits}
              aria-label="elevation units"
            >
              <ToggleButton value="m" aria-label="meters">
                {intl.formatMessage({ id: "m" })}
              </ToggleButton>
              <ToggleButton value="feet" aria-label="feet">
                {intl.formatMessage({ id: "feet" })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" m={1}>
            <Typography variant="body2">
              {intl.formatMessage({ id: "form.preferedSport" })}
            </Typography>
            <ToggleButtonGroup
              value={preferedSport}
              exclusive
              onChange={handleChangePreferedSport}
              aria-label="elevation units"
            >
              <ToggleButton value="run">
                <DirectionsRun />
              </ToggleButton>
              <ToggleButton value="bike">
                <DirectionsBike />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" m={1}>
            <Typography variant="body2">
              {intl.formatMessage({ id: "form.temperatureUnits" })}
            </Typography>
            <ToggleButtonGroup
              value={temperatureUnits}
              exclusive
              onChange={handleChangeTemperatureUnits}
              aria-label="temperature units"
            >
              <ToggleButton value="C" aria-label="celsius">
                °C
              </ToggleButton>
              <ToggleButton value="F" aria-label="fahrenheit">
                °F
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {
            error && <Alert severity="error">{error}</Alert>
          }
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            loading={loading}
          >
            {intl.formatMessage({ id: "action.save" })}
          </LoadingButton>

          <Button
            fullWidth
            onClick={userService.signOutUser}
          >
            {intl.formatMessage({ id: "account.signOut" })}
          </Button>

          {userService.user.subscriptionStatus === "active" && (
            <Button
              fullWidth
              onClick={() => navigate("/subscription")}
              sx={{ mt: 1 }}
              size="small"
              color="inherit"
            >
              {intl.formatMessage({ id: "account.manageSubscription" })}
            </Button>
          )}
        </Box>
      </Box>)}
    </ImageColumGrid>
  );
});

export default Account;
